import React, { useState, useEffect } from 'react';

interface DebugLog {
  id: string;
  timestamp: Date;
  type: 'mcp_request' | 'mcp_response' | 'openai_request' | 'openai_response' | 'error';
  data: any;
  duration?: number;
}

interface MCPDebugLoggerProps {
  className?: string;
}

const MCPDebugLogger: React.FC<MCPDebugLoggerProps> = ({ className = '' }) => {
  const [logs, setLogs] = useState<DebugLog[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // Show in both development and production for debugging
  useEffect(() => {
    setIsVisible(true); // Always visible for debugging AI issues
  }, []);

  // Listen for debug events
  useEffect(() => {
    const handleDebugEvent = (event: CustomEvent) => {
      const { type, data, duration } = event.detail;
      const newLog: DebugLog = {
        id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
        timestamp: new Date(),
        type,
        data,
        duration,
      };

      setLogs(prev => [newLog, ...prev.slice(0, 49)]); // Keep last 50 logs
    };

    window.addEventListener('mcp-debug' as any, handleDebugEvent);
    return () => window.removeEventListener('mcp-debug' as any, handleDebugEvent);
  }, []);

  const clearLogs = () => {
    setLogs([]);
  };

  const copyAllLogs = async () => {
    const logText = logs.map(log => {
      const timestamp = log.timestamp.toISOString();
      const duration = log.duration ? ` (${log.duration}ms)` : '';
      const typeLabel = getTypeLabel(log.type);
      const data = formatData(log.data);
      
      return `[${timestamp}] ${typeLabel}${duration}\n${data}\n${'='.repeat(80)}`;
    }).join('\n\n');

    const fullReport = `MCP Debug Logger Report
Generated: ${new Date().toISOString()}
Total Logs: ${logs.length}
${'='.repeat(80)}

${logText}

${'='.repeat(80)}
End of Report`;

    try {
      await navigator.clipboard.writeText(fullReport);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy logs:', err);
      // Fallback: create a text area and select it
      const textArea = document.createElement('textarea');
      textArea.value = fullReport;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  const toggleFullScreen = () => {
    setIsFullScreen(!isFullScreen);
    if (!isFullScreen) {
      setIsExpanded(true);
    }
  };

  const getTypeColor = (type: DebugLog['type']) => {
    switch (type) {
      case 'mcp_request':
        return 'text-blue-600 bg-blue-50';
      case 'mcp_response':
        return 'text-blue-800 bg-blue-100';
      case 'openai_request':
        return 'text-green-600 bg-green-50';
      case 'openai_response':
        return 'text-green-800 bg-green-100';
      case 'error':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const formatData = (data: any) => {
    if (typeof data === 'string') return data;
    return JSON.stringify(data, null, 2);
  };

  const getTypeLabel = (type: DebugLog['type']) => {
    switch (type) {
      case 'mcp_request':
        return 'MCP →';
      case 'mcp_response':
        return 'MCP ←';
      case 'openai_request':
        return 'GPT →';
      case 'openai_response':
        return 'GPT ←';
      case 'error':
        return 'ERR';
      default:
        return type;
    }
  };

  if (!isVisible) return null;

  return (
    <div className={`fixed bottom-4 left-4 z-50 hidden md:block ${className}`}>
      {/* Minimized state */}
      {!isExpanded && (
        <button
          onClick={() => setIsExpanded(true)}
          className="bg-black text-white px-3 py-2 rounded-lg text-sm font-mono shadow-lg hover:bg-gray-800 transition-colors flex items-center space-x-2"
        >
          <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
          <span>MCP Debug ({logs.length})</span>
        </button>
      )}

      {/* Expanded state */}
      {isExpanded && (
        <div className={`bg-black text-white rounded-lg shadow-2xl flex flex-col ${
          isFullScreen 
            ? 'fixed inset-4 z-50 w-auto h-auto' 
            : 'w-96 max-h-96'
        }`}>
          {/* Header */}
          <div className="flex items-center justify-between p-3 border-b border-gray-700">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="font-mono text-sm font-medium">MCP Debug Logger</span>
              <span className="text-xs text-gray-400">({logs.length})</span>
            </div>
            <div className="flex items-center space-x-1">
              <button
                onClick={copyAllLogs}
                className={`text-xs px-2 py-1 rounded transition-colors ${
                  copySuccess 
                    ? 'bg-green-600 text-white' 
                    : 'text-gray-400 hover:text-white hover:bg-gray-700'
                }`}
                title="Copy all logs to clipboard"
              >
                {copySuccess ? '✓ Copied!' : 'Copy All'}
              </button>
              <button
                onClick={toggleFullScreen}
                className="text-gray-400 hover:text-white text-xs px-2 py-1 rounded hover:bg-gray-700"
                title={isFullScreen ? "Exit full screen" : "Full screen"}
              >
                {isFullScreen ? (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                  </svg>
                )}
              </button>
              <button
                onClick={clearLogs}
                className="text-gray-400 hover:text-white text-xs px-2 py-1 rounded hover:bg-gray-700"
                title="Clear logs"
              >
                Clear
              </button>
              {!isFullScreen && (
                <button
                  onClick={() => setIsExpanded(false)}
                  className="text-gray-400 hover:text-white"
                  title="Minimize"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Logs */}
          <div className={`flex-1 overflow-y-auto p-2 space-y-1 ${
            isFullScreen ? 'max-h-none' : 'max-h-80'
          }`}>
            {logs.length === 0 ? (
              <div className="text-center text-gray-500 py-4 text-xs">
                No debug logs yet. Try using the AI Material Search.
              </div>
            ) : (
              logs.map((log) => (
                <div key={log.id} className="text-xs border border-gray-700 rounded p-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className={`px-2 py-1 rounded text-xs font-mono ${getTypeColor(log.type)}`}>
                      {getTypeLabel(log.type)}
                    </span>
                    <div className="flex items-center space-x-2 text-gray-400">
                      {log.duration && (
                        <span className="text-xs">{log.duration}ms</span>
                      )}
                      <span className="text-xs">
                        {log.timestamp.toLocaleTimeString()}
                      </span>
                    </div>
                  </div>
                  <div className={`text-gray-300 font-mono overflow-x-auto overflow-y-auto ${
                    isFullScreen ? 'text-sm max-h-40' : 'text-xs max-h-20'
                  }`}>
                    <pre className="whitespace-pre-wrap break-words">
                      {formatData(log.data)}
                    </pre>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="p-2 border-t border-gray-700 text-xs text-gray-400">
            <div className="flex justify-between">
              <span>Development Mode</span>
              <span>Last: {logs[0]?.timestamp.toLocaleTimeString() || 'None'}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MCPDebugLogger;
